import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { canAccess, roleLabel, userRoles } from "@/lib/constants";
import { useEffect, useState } from "react";
import NotificationToggle from "@/components/NotificationToggle";
import {
  SquaresFour, Buildings, Storefront, ClipboardText, Package,
  Barcode, Archive, Truck, Lifebuoy, Note, ArrowUUpLeft,
  UsersThree, ChartBar, SignOut, ShoppingCart, Receipt, MapPin, LockKey, Printer, Van, FileXls, Megaphone, List, X, Folders,
} from "@phosphor-icons/react";

const ALL_ROLES = ["admin","accounts","salesman","picker","scanner","packer","collection"];

const NAV = [
  { to: "/",            label: "Dashboard",            icon: SquaresFour,   roles: ALL_ROLES },
  { to: "/companies",   label: "Companies",            icon: Buildings,     roles: ["admin"] },
  { to: "/retailers",   label: "Retailers",            icon: Storefront,    roles: ["admin","accounts"] },
  { to: "/transporters",label: "Transporters",         icon: Van,           roles: ["admin","accounts"] },
  { to: "/orders",      label: "Orders",               icon: ClipboardText, roles: ["admin","accounts","salesman"] },
  { to: "/picking",     label: "Material Picking",     icon: Package,       roles: ["admin","picker"] },
  { to: "/scanning",    label: "Scanning & Count",     icon: Barcode,       roles: ["admin","scanner"] },
  { to: "/packing",     label: "Packing",              icon: Archive,       roles: ["admin","packer"] },
  { to: "/dispatch",    label: "Dispatch",             icon: Truck,         roles: ["admin","accounts"] },
  { to: "/print-label", label: "Print Dispatch Label", icon: Printer,       roles: ["admin","accounts"] },
  { to: "/print-grsr-label", label: "Print GRSR Label",icon: Printer,       roles: ["admin","accounts"] },
  { to: "/print-box-label",  label: "Print Box Label", icon: Printer,       roles: ["admin"] },
  { to: "/support",     label: "Support",              icon: Lifebuoy,      roles: ALL_ROLES },
  { to: "/documents",   label: "Documents",            icon: Folders,       roles: ALL_ROLES },
  { to: "/box-notes",   label: "Box Notes",            icon: Note,          roles: ["admin","accounts"] },
  { to: "/returns",     label: "Returns",              icon: ArrowUUpLeft,  roles: ["admin"] },
  { to: "/purchases",   label: "Purchase",             icon: ShoppingCart,  roles: ["admin"] },
  { to: "/grsr",        label: "GRSR",                 icon: Receipt,       roles: ["admin","accounts"] },
  { to: "/sales-file",  label: "File Match",           icon: FileXls,       roles: ["admin","accounts"] },
  { to: "/visits",      label: "Retailer Visits",      icon: MapPin,        roles: ["admin","collection"] },
  { to: "/users",       label: "Users",                icon: UsersThree,    roles: ["admin"] },
  { to: "/admin-notes", label: "Admin Notes",          icon: Megaphone,     roles: ["admin"] },
  { to: "/reports",     label: "Reports",              icon: ChartBar,      roles: ["admin","accounts"] },
  { to: "/change-password", label: "Change Password",  icon: LockKey,       roles: ALL_ROLES },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);   // mobile drawer state
  const items = NAV.filter((n) => canAccess(user, n.roles));
  const roleList = userRoles(user);

  // Close drawer on route change (mobile)
  useEffect(() => { setOpen(false); }, [location.pathname]);
  // Lock body scroll while drawer open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else      document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* Mobile top bar (hamburger + brand) — hidden on md+ */}
      <div className="md:hidden fixed top-0 inset-x-0 h-14 z-30 bg-orange-50 border-b border-orange-200 flex items-center px-3 gap-2"
           style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
           data-testid="mobile-topbar">
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(true)}
          className="h-10 w-10 grid place-items-center rounded-sm text-orange-950 hover:bg-orange-100"
          aria-label="Open menu">
          <List size={22} weight="bold"/>
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.3em] text-orange-600 font-bold leading-none">ORDER & DISPATCH</div>
          <div className="font-display text-base font-black text-orange-950 leading-tight truncate">DispatchHub</div>
        </div>
        <NotificationToggle compact />
        <div className="h-9 w-9 rounded-full bg-orange-600 text-white grid place-items-center font-display font-bold text-sm">
          {user.name?.[0]?.toUpperCase() || "U"}
        </div>
        <button
          data-testid="logout-btn-mobile"
          onClick={() => { logout(); nav("/login"); }}
          className="h-9 w-9 grid place-items-center rounded-sm text-orange-950 hover:bg-orange-100"
          aria-label="Logout" title="Logout"
        >
          <SignOut size={18} weight="bold"/>
        </button>
      </div>

      {/* Backdrop (mobile only) */}
      {open && (
        <div
          data-testid="mobile-backdrop"
          onClick={() => setOpen(false)}
          className="md:hidden fixed inset-0 bg-slate-900/50 z-40"
          aria-hidden="true"
        />
      )}

      <aside
        data-testid="sidebar"
        className={`w-64 shrink-0 bg-orange-50 text-orange-900 h-screen sticky top-0 flex-col border-r border-orange-200
                   hidden md:flex
                   ${open ? "!fixed !inset-y-0 !left-0 !flex z-50" : ""}`}
        style={open ? { paddingTop: "env(safe-area-inset-top, 0px)" } : {}}
      >
        {/* Close (mobile drawer only) */}
        {open && (
          <button
            data-testid="mobile-menu-close"
            onClick={() => setOpen(false)}
            className="md:hidden absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-sm text-orange-950 hover:bg-orange-100"
            aria-label="Close menu">
            <X size={20} weight="bold"/>
          </button>
        )}
        <div className="px-6 py-6 border-b border-orange-200">
          <div className="text-xs tracking-[0.3em] text-orange-600 font-bold">ORDER & DISPATCH</div>
          <div className="font-display text-2xl font-black text-orange-950 mt-1">Control Room</div>
        </div>
      <nav className="flex-1 overflow-y-auto py-4">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`nav-${it.to.replace('/', '') || 'home'}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "text-orange-950 border-orange-600 bg-orange-100 font-semibold"
                    : "border-transparent text-orange-800 hover:text-orange-950 hover:bg-orange-100"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              <span>{it.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-orange-200 p-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-orange-600 text-white grid place-items-center font-display font-bold text-sm">
            {user.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-orange-950 font-semibold truncate">{user.name}</div>
            <div className="text-[11px] text-orange-700 truncate flex flex-wrap gap-1 mt-0.5" data-testid="sidebar-user-roles">
              {roleList.map((r) => (
                <span key={r} className="inline-flex items-center px-1.5 py-0 rounded-sm bg-orange-200/60 text-orange-900 font-semibold">
                  {roleLabel(r)}
                </span>
              ))}
            </div>
          </div>
          <NotificationToggle />
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); nav("/login"); }}
            className="p-2 rounded-sm hover:bg-orange-200 text-orange-800 hover:text-orange-950"
            title="Logout"
          >
            <SignOut size={18} weight="bold" />
          </button>
        </div>
      </div>
    </aside>
    </>
  );
}
